import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeftIcon, AcademicCapIcon, ExclamationCircleIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { createExam } from "../../services/Exams/ExamsService";
import { getApiErrorMessage } from "../../utils/apiError";
import WordPrintFields, { fieldErrorText, printCodeProblem, wordPrintPayload } from "./word/WordPrintFields";

const inputBaseClass =
  'block h-10 w-full rounded-lg border px-3 text-sm text-slate-900 shadow-sm transition placeholder:text-slate-400 hover:border-slate-400 focus:ring-4 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-500 max-sm:h-11';
const inputOkClass = 'border-slate-300 focus:border-blue-600 focus:ring-blue-600/15';
const inputErrorClass = 'border-red-300 focus:border-red-500 focus:ring-red-500/15';
const labelClass = 'mb-1.5 block text-sm font-medium text-slate-700';

// The form's fields by the server's names, with the element each error sends the focus to (in the form's order).
const FIELD_IDS = {
  name: 'name',
  description: 'description',
  duration_minutes: 'duration_minutes',
  passing_score: 'passing_score',
  language: 'create-exam-language',
  print_code: 'create-exam-print-code',
};
// An error under its field, tied to it (aria-describedby).
const errorId = (field) => `${FIELD_IDS[field]}-error`;

const CreateExam = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);

  // language "" and print_code "": none (the Word prints Russian, its footer no variant).
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    duration_minutes: 60,
    passing_score: 70,
    language: '',
    print_code: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [step, setStep] = useState(''); // For displaying current creation step

  // A field changed (by its name): its value, and its error goes.
  const setField = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    setField(name, type === 'number' ? parseInt(value) || 0 : value);
  };

  // The first field with an error takes the focus (in the form's order), once the form is enabled again (it is
  // disabled while the exam is being created).
  const [errorField, setErrorField] = useState(null);
  useEffect(() => {
    if (!errorField || creating) return;
    document.getElementById(FIELD_IDS[errorField])?.focus();
    setErrorField(null);
  }, [errorField, creating]);
  const focusFirstError = (errors) => {
    const first = Object.keys(FIELD_IDS).find((field) => errors[field]);
    if (first) setErrorField(first);
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = t('exam_bank.form.name_required');
    }

    if (formData.duration_minutes <= 0) {
      errors.duration_minutes = t('exam_bank.form.duration_invalid');
    }

    if (formData.passing_score < 0 || formData.passing_score > 100) {
      errors.passing_score = t('exam_bank.form.passing_score_invalid');
    }

    const codeProblem = printCodeProblem(t, formData.print_code);
    if (codeProblem) {
      errors.print_code = codeProblem;
    }

    setFormErrors(errors);
    focusFirstError(errors);
    return Object.keys(errors).length === 0;
  };

  const handleBackToExams = () => {
    navigate('/exams_list');
  };

  const handleCancel = () => {
    navigate('/exams_list');
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setCreating(true);
      setError(null);

      const examData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        duration_minutes: formData.duration_minutes,
        passing_score: formData.passing_score,
        ...wordPrintPayload(formData)
      };

      setStep(t('exam_bank.create.creating_exam'));
      const createdExam = await createExam({ data: examData });
      console.log('Exam created:', createdExam);

      // Redirect to the created exam
      navigate(`/exams_list/${createdExam.uuid}`);

    } catch (err) {
      console.error('Error:', err);
      const data = err?.response?.data;
      // A 400 {field: [messages]} (no `detail`): each under its field (the first takes the focus), any other in
      // the message above the form.
      if (err?.response?.status === 400 && data && typeof data === 'object' && !Array.isArray(data) && typeof data.detail !== 'string') {
        const fieldErrors = {};
        const others = [];
        Object.entries(data).forEach(([field, messages]) => {
          const text = fieldErrorText(messages);
          if (FIELD_IDS[field] && text) fieldErrors[field] = text;
          else if (text) others.push(`${field}: ${text}`);
        });
        setFormErrors(fieldErrors);
        focusFirstError(fieldErrors);
        setError(others.length ? t('exam_bank.view.validation_error', { details: others.join('; ') }) : null);
      } else {
        setError(getApiErrorMessage(t, err, 'exam_bank.create.generic_error'));
      }
    } finally {
      setCreating(false);
      setStep('');
    }
  };

  return (
    <div className="mx-auto w-full max-w-2xl motion-safe:animate-fade-in">
      {/* Back button */}
      <button
        type="button"
        onClick={handleBackToExams}
        className="mb-2 inline-flex min-h-[2.75rem] items-center gap-1.5 rounded-lg text-sm font-medium text-slate-500 transition duration-200 hover:text-slate-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 md:mb-4 md:min-h-0"
      >
        <ArrowLeftIcon className="h-4 w-4" />
        {t('exam_taking.back_to_exams')}
      </button>

      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        {/* Header */}
        <div className="flex items-start gap-4 border-b border-slate-200 p-6">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600" aria-hidden="true">
            <AcademicCapIcon className="h-6 w-6" />
          </div>
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
              {t('exam_bank.create.title')}
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              {t('exam_bank.create.subtitle')}
            </p>
          </div>
        </div>

        {/* A new exam is always a draft (the API sets the status): it is activated on its page. */}
        <p className="flex items-start gap-2 border-b border-slate-200 bg-amber-50/70 px-6 py-3 text-sm text-amber-900">
          <InformationCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
          {t('exam_bank.create.draft_note')}
        </p>

        <div className="p-6">
          {/* Error message */}
          {error && (
            <div role="alert" className="mb-6 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-4">
              <ExclamationCircleIcon className="h-5 w-5 shrink-0 text-red-500" aria-hidden="true" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Form */}
          <div className="space-y-5">
            {/* Exam name */}
            <div>
              <label htmlFor="name" className={labelClass}>
                {t('exam_bank.form.name')} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder={t('exam_bank.form.name_placeholder')}
                disabled={creating}
                aria-invalid={formErrors.name ? true : undefined}
                aria-describedby={formErrors.name ? errorId('name') : undefined}
                className={`${inputBaseClass} ${
                  formErrors.name ? inputErrorClass : inputOkClass
                }`}
              />
              {formErrors.name && (
                <p id={errorId('name')} className="mt-1.5 text-sm text-red-600">{formErrors.name}</p>
              )}
            </div>

            {/* Description */}
            <div>
              <label htmlFor="description" className={labelClass}>
                {t('exam_bank.form.description')}
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder={t('exam_bank.form.description_placeholder')}
                rows={4}
                disabled={creating}
                aria-invalid={formErrors.description ? true : undefined}
                aria-describedby={formErrors.description ? errorId('description') : undefined}
                className="block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm transition placeholder:text-slate-400 hover:border-slate-400 focus:border-blue-600 focus:ring-4 focus:ring-blue-600/15 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-500"
              />
              {formErrors.description && (
                <p id={errorId('description')} className="mt-1.5 text-sm text-red-600">{formErrors.description}</p>
              )}
            </div>

            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              {/* Duration */}
              <div>
                <label htmlFor="duration_minutes" className={labelClass}>
                  {t('exam_bank.form.duration')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  id="duration_minutes"
                  name="duration_minutes"
                  value={formData.duration_minutes}
                  onChange={handleInputChange}
                  min="1"
                  disabled={creating}
                  aria-invalid={formErrors.duration_minutes ? true : undefined}
                  aria-describedby={formErrors.duration_minutes ? errorId('duration_minutes') : undefined}
                  className={`${inputBaseClass} ${
                    formErrors.duration_minutes ? inputErrorClass : inputOkClass
                  }`}
                />
                {formErrors.duration_minutes && (
                  <p id={errorId('duration_minutes')} className="mt-1.5 text-sm text-red-600">{formErrors.duration_minutes}</p>
                )}
              </div>

              {/* Passing score */}
              <div>
                <label htmlFor="passing_score" className={labelClass}>
                  {t('exam_bank.form.passing_score')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  id="passing_score"
                  name="passing_score"
                  value={formData.passing_score}
                  onChange={handleInputChange}
                  min="0"
                  max="100"
                  disabled={creating}
                  aria-invalid={formErrors.passing_score ? true : undefined}
                  aria-describedby={[formErrors.passing_score ? errorId('passing_score') : '', 'passing_score-hint'].filter(Boolean).join(' ')}
                  className={`${inputBaseClass} ${
                    formErrors.passing_score ? inputErrorClass : inputOkClass
                  }`}
                />
                {formErrors.passing_score && (
                  <p id={errorId('passing_score')} className="mt-1.5 text-sm text-red-600">{formErrors.passing_score}</p>
                )}
                <p id="passing_score-hint" className="mt-1.5 text-xs text-slate-500">
                  {t('exam_bank.form.passing_score_hint')}
                </p>
              </div>
            </div>

            {/* The Word's language and variant code (its fixed labels, its footer); none of them is required. */}
            <WordPrintFields
              idPrefix="create-exam"
              language={formData.language}
              printCode={formData.print_code}
              onChange={setField}
              errors={{ language: formErrors.language, print_code: formErrors.print_code }}
              disabled={creating}
              className="border-t border-slate-200 pt-5"
            />
          </div>
        </div>

        {/* Buttons: Cancel, then the one primary; one row on phones too, the primary filling it (44px). */}
        <div className="flex items-center justify-end gap-3 rounded-b-2xl border-t border-slate-200 bg-slate-50 px-6 py-4">
          <button
            type="button"
            onClick={handleCancel}
            disabled={creating}
            className="inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-white px-4 text-sm font-medium text-slate-700 shadow-sm ring-1 ring-inset ring-slate-300 transition duration-200 hover:bg-slate-50 hover:text-slate-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:cursor-not-allowed disabled:opacity-50 sm:h-10"
          >
            {t('buttons.cancel')}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={creating}
            className="inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 text-sm font-medium text-white shadow-sm shadow-blue-600/25 transition duration-200 hover:bg-blue-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:cursor-not-allowed disabled:opacity-50 max-sm:flex-1 sm:h-10"
          >
            {creating && (
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white"></div>
            )}
            {creating ? (step || t('exam_bank.create.creating')) : t('exam_bank.create.submit')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateExam;
